import { Button, Input, Select, NothingToShow } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { DenseTable } from '../components/denseTable/DenseTable';
import { DenseTableColumn } from '../components/denseTable/DenseTable.types';
import { dodCommandLabel } from '../components/dodCommands';
import { StateChip } from '../components/stateChip/StateChip';
import { formatTimestamp } from '../overviewTab/dodFormat';

import * as Styled from './HistoryTab.styles';
import { HistoryTabProps } from './HistoryTab.types';
import { HISTORY_PAGE_SIZES, HistoryRow, HistorySource, HistoryStatus } from './history.helpers';

const EMPTY_CELL = '—';

/**
 * Historia wysłanych rozkazów.
 *
 * Every column but the status renders a plain string, so the shared `ui/table/Table` puts it
 * through `Table.RowItem` and the row gets the application's typography, its ellipsis and its
 * tooltip for free. The details panel is NOT rendered here: the container publishes it through
 * `setDetails`, and PageLayout draws it beside the content like every other list page.
 */
export const HistoryTab = ({
  rows,
  isLoading,
  isError,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  source,
  status,
  search,
  onSourceChange,
  onStatusChange,
  onSearchChange,
  pendingCount,
  onShowPending,
  isLive,
  selectedRow,
  onSelectRow,
  onExportCsv,
  isExporting,
}: HistoryTabProps): JSX.Element => {
  const { formatMessage } = useLocale();

  const sourceLabel = (row: HistoryRow): string =>
    formatMessage({
      id: AppMessages[row.source === 'dod' ? 'dod.history.retransmission' : 'dod.history.directSend'],
    });

  const columns: Array<DenseTableColumn<HistoryRow>> = [
    {
      key: 'time',
      width: '150px',
      header: formatMessage({ id: AppMessages['dod.history.column.time'] }),
      render: (row) => formatTimestamp(row.timestamp),
    },
    {
      key: 'source',
      width: '110px',
      header: formatMessage({ id: AppMessages['dod.history.column.source'] }),
      render: (row) => sourceLabel(row),
    },
    {
      key: 'rcpTkd',
      width: 'minmax(110px, 1.2fr)',
      header: formatMessage({ id: AppMessages['dod.history.column.rcpTkd'] }),
      render: (row) => `${row.rcpLabel} · ${row.tkdLabel}`,
    },
    {
      key: 'command',
      width: 'minmax(110px, 1.4fr)',
      header: formatMessage({ id: AppMessages['dod.history.column.command'] }),
      render: (row) => row.commandName || dodCommandLabel(row.commandType),
    },
    {
      key: 'sequence',
      width: '60px',
      header: formatMessage({ id: AppMessages['dod.history.column.sequence'] }),
      render: (row) => row.sequenceNumber,
    },
    {
      key: 'status',
      width: '120px',
      header: formatMessage({ id: AppMessages['dod.history.column.status'] }),
      render: (row) => (
        <StateChip
          state={row.status}
          tone={row.status === 'OK' ? 'ok' : 'bad'}
          text={formatMessage({ id: AppMessages[`dod.history.status.${row.status}`] })}
        />
      ),
    },
    {
      key: 'error',
      width: 'minmax(110px, 2fr)',
      header: formatMessage({ id: AppMessages['dod.history.column.error'] }),
      render: (row) => (row.errorMessage ? row.errorMessage : EMPTY_CELL),
    },
    {
      key: 'duration',
      width: '90px',
      header: formatMessage({ id: AppMessages['dod.history.column.duration'] }),
      render: (row) => `${row.durationMs} ms`,
    },
  ];

  return (
    <>
      <Styled.LiveBar>
        {isLive && <Styled.LiveDot>{formatMessage({ id: AppMessages['dod.history.live'] })}</Styled.LiveDot>}

        {pendingCount > 0 ? (
          <Button variant="outlined" size="small" onClick={onShowPending} aria-live="polite">
            {formatMessage({ id: AppMessages['dod.history.live.new'] }, { count: pendingCount })}
          </Button>
        ) : (
          <Styled.LiveHint aria-live="polite">
            {formatMessage({ id: AppMessages['dod.history.live.buffered'] })}
          </Styled.LiveHint>
        )}

        <Styled.Spacer />

        <Button variant="outlined" onClick={onExportCsv} disabled={isExporting || total === 0}>
          {formatMessage({ id: AppMessages['dod.history.export'] })}
        </Button>
      </Styled.LiveBar>

      <Styled.FilterRow>
        <Styled.FilterField>
          <Select
            label={formatMessage({ id: AppMessages['dod.history.filter.source'] })}
            variant="outlined"
            value={source}
            onChange={(event) => onSourceChange(event.target.value as HistorySource | '')}
            options={[
              { label: formatMessage({ id: AppMessages['dod.filter.all'] }), value: '' },
              { label: formatMessage({ id: AppMessages['dod.history.directSend'] }), value: 'direct' },
              { label: formatMessage({ id: AppMessages['dod.history.retransmission'] }), value: 'dod' },
            ]}
          />
        </Styled.FilterField>

        <Styled.FilterField>
          <Select
            label={formatMessage({ id: AppMessages['dod.history.filter.status'] })}
            variant="outlined"
            value={status}
            onChange={(event) => onStatusChange(event.target.value as HistoryStatus | '')}
            options={[
              { label: formatMessage({ id: AppMessages['dod.filter.all'] }), value: '' },
              { label: formatMessage({ id: AppMessages['dod.history.status.OK'] }), value: 'OK' },
              { label: formatMessage({ id: AppMessages['dod.history.status.ERROR'] }), value: 'ERROR' },
            ]}
          />
        </Styled.FilterField>

        <Styled.FilterField>
          <Input
            variant="outlined"
            placeholder={formatMessage({ id: AppMessages['dod.history.filter.search'] })}
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            withoutHelperText
            testId="dod-history-search"
          />
        </Styled.FilterField>
      </Styled.FilterRow>

      {isError ? (
        <div role="alert">
          <NothingToShow text={formatMessage({ id: AppMessages['dod.common.error'] })} />
        </div>
      ) : (
        <DenseTable<HistoryRow>
          columns={columns}
          data={rows}
          isLoading={isLoading}
          nothingToShowText={formatMessage({ id: AppMessages['dod.history.empty'] })}
          onClickRow={onSelectRow}
          activeRowId={selectedRow?.id}
          pagination={{
            page,
            count: total,
            rowsPerPage: pageSize,
            setPage: onPageChange,
            setRowsPerPage: onPageSizeChange,
            rowsPerPageOptions: HISTORY_PAGE_SIZES,
          }}
        />
      )}
    </>
  );
};
